import {Course, SchoolClass, SchoolClassCourse} from "models"
import {memo, ShapeComponent, shapeComponent, View} from "shared/base"
import React, {useMemo} from "react"
import CkeditorPresentation from "ckeditor/presentation"
import CourseModuleBox from "components/user/school-class-courses/show/course-module-box"
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome"
import Link from "@kaspernj/api-maker/build/link"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import {useFrontLayout} from "components/front-layout"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import useQueryParams from "on-location-changed/build/use-query-params"

/** @typedef {object} RoutesUserSchoolClassCoursesShowProps */

/** @typedef {object} RoutesUserSchoolClassCoursesShowState */

/**
 * @extends {ShapeComponent<RoutesUserSchoolClassCoursesShowProps, RoutesUserSchoolClassCoursesShowState>}
 */
class RoutesUserSchoolClassCoursesShow extends ShapeComponent {
  setup() {
    setLayout("user")

    const {locale, t} = useI18n({namespace: "js.routes.user.school_class_courses.show"})
    const currentUser = useCurrentUser()
    const params = useParams()
    const queryParams = useQueryParams()
    const {course} = useModel(Course, {
      cacheArgs: [currentUser?.id()],
      match: {params},
      preload: ["brain_break", "challenge", "check_in", "course_groups", "visible_course_modules"],
      select: {
        BrainBreak: ["id"],
        Challenge: ["id"],
        CheckIn: ["id"],
        Course: ["backgroundImageUrl", "description", "id", "name"],
        CourseGroup: ["id", "identifier"],
        CourseModule: ["appetizer", "expectedMinutes", "id", "name", "position"]
      }
    })
    const {schoolClass: loadedSchoolClass} = useModel(SchoolClass, {
      active: Boolean(queryParams.school_class_id),
      cacheArgs: [currentUser?.id()],
      loadByQueryParam: ({queryParams}) => queryParams.school_class_id,
      preload: ["school.account", "school.country"],
      select: {
        Account: ["id"],
        Country: ["countryCode"],
        School: ["id", "name"],
        SchoolClass: ["hasProSubscription", "id", "interpretedName", "schoolId"]
      }
    })

    const {schoolClassCourse} = useModel(SchoolClassCourse, {
      active: Boolean(queryParams.school_class_course_id),
      cacheArgs: [currentUser?.id()],
      loadByQueryParam: ({queryParams}) => queryParams.school_class_course_id,
      preload: ["school_class.school.account", "school_class.school.country", "school_class_course_modules.team_survey.survey.steps"],
      select: {
        Account: ["id"],
        Country: ["countryCode"],
        School: ["id", "name"],
        SchoolClass: ["hasProSubscription", "id", "interpretedName", "schoolId"],
        SchoolClassCourse: ["id", "schoolClassId", "state"],
        SchoolClassCourseModule: ["courseModuleId", "id", "state"],
        Survey: ["id"],
        SurveyStep: ["id", "position"],
        TeamSurvey: ["id", "preview"]
      }
    })

    const schoolClass = loadedSchoolClass || schoolClassCourse?.schoolClass()

    this.course = course
    this.locale = locale
    this.queryParams = queryParams
    this.schoolClass = schoolClass
    this.schoolClassCourse = schoolClassCourse
    this.t = t

    const topLeft = useMemo(() => schoolClass && (
      <Link
        style={this.topLeftLinkStyle ||= {color: "#000", textShadow: "0 0 6px #fff"}}
        to={Routes.userSchoolClassClassSpacePath(schoolClass.id())}
      >
        <FontAwesomeIcon name="chevron-left" style={this.topLeftIconStyle ||= {marginRight: 5, color: "#000", textShadow: "0 0 6px #fff"}} />
        {t(".go_to_the_space_of_the_class")}
      </Link>
    ), [locale, schoolClass?.id()])

    useFrontLayout()?.setState({
      background: course?.backgroundImageUrl(),
      headerStyle: {color: "#fff"},
      headTitle: course?.name() || Course.modelName().human(),
      menu: false,
      requireUser: true,
      schoolClass,
      topLeft,
      proTrialTopBar: false,
      userMenu: false,
      weekBar: false
    })
  }

  render() {
    const {course, queryParams, schoolClass, schoolClassCourse} = this.tt
    const waitingForSchoolClass = Boolean(queryParams.school_class_id || queryParams.school_class_course_id) && !schoolClass
    const waitingForSchoolClassCourse = Boolean(queryParams.school_class_course_id) && !schoolClassCourse

    let sortedCourseModules

    if (course) {
      sortedCourseModules = course.visibleCourseModules().loaded().sort((courseModule1, courseModule2) =>
        courseModule1.position() - courseModule2.position()
      )
    }

    return (
      <View dataSet={this.rootViewDataSet ||= {route: "user/courses/show"}}>
        {course?.hasDescription() &&
          <CkeditorPresentation html={course.description()} style={this.ckeditorPresentationStyle ||= {color: "#fff", textAlign: "center"}} />
        }
        <div className="grid grid-cols-lg-4 grid-gap-10 course-modules-grid">
          {!waitingForSchoolClass && !waitingForSchoolClassCourse && sortedCourseModules?.map((courseModule) =>
            <div className="col-span-1 course-module-box-container" key={courseModule.id()}>
              <CourseModuleBox course={course} courseModule={courseModule} schoolClass={schoolClass} schoolClassCourse={schoolClassCourse} />
            </div>
          )}
        </div>
      </View>
    )
  }
}

export default memo(shapeComponent(RoutesUserSchoolClassCoursesShow))
